'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  company: string;
  jobTitle: string;
  service: string;
  message: string;
  inquiryType: string;
}

const initialForm: FormData = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  company: '',
  jobTitle: '',
  service: '',
  message: '',
  inquiryType: 'client',
};

const services = [
  'IT Staffing Solutions',
  'Executive Search',
  'Contract Staffing',
  'Direct Hire Placement',
  'IT Consulting',
  'Workforce Solutions',
  'Other',
];

const offices = [
  {
    city: 'Sterling, VA',
    address: '24076 Gumspring Kiln Ter',
    cityState: 'Sterling, VA 20166',
    phone: '+1 (214) 555-0100',
    email: 'sterling@tranceformright.com',
    isHQ: true,
  },
  {
    city: 'Chicago, IL',
    address: '20 N Wacker Drive Suite 1000',
    cityState: 'Chicago, IL 60606',
    phone: '+1 (312) 555-0300',
    email: 'chicago@tranceformright.com',
    isHQ: false,
  },
  {
    city: 'New York, NY',
    address: '438 11th Ave',
    cityState: 'New York, NY 10018',
    phone: '+1 (212) 555-0200',
    email: 'newyork@tranceformright.com',
    isHQ: false,
  },
];

const faqs = [
  {
    q: 'How quickly can TranceformRIGHT provide qualified candidates?',
    a: 'For most IT roles, we can present pre-vetted candidates within 48-72 hours. For specialized or senior positions, our typical turnaround is 5-7 business days.',
  },
  {
    q: 'What industries does TranceformRIGHT specialize in?',
    a: 'We serve Fortune 500 companies across 12+ industries including Financial Services, Healthcare, Technology, Retail, Energy, Government, Manufacturing, and Telecommunications.',
  },
  {
    q: 'Do you offer placement guarantees?',
    a: 'Yes. We offer a 90-day replacement guarantee on direct hire placements and a 12-month guarantee on executive search engagements at no additional cost.',
  },
  {
    q: 'Can TranceformRIGHT handle large-scale staffing projects?',
    a: 'Absolutely. We have successfully deployed teams of 150+ professionals within 90 days. Our Workforce Solutions practice specializes in high-volume and complex staffing programs.',
  },
  {
    q: 'How does TranceformRIGHT vet its candidates?',
    a: 'Every candidate undergoes technical skills assessment, behavioral interviews, reference checks, background verification, and cultural fit evaluation before being presented to clients.',
  },
];

export default function ContactPage() {
  const [form, setForm] = useState<FormData>(initialForm);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      setSubmitted(true);
    }, 1500);
  };

  return (
    <div className="min-h-screen" style={{ background: '#ffffff' }}>
      <Navbar />

      {/* Page Hero - Pinnacle1 style */}
      <section
        className="relative overflow-hidden"
        style={{
          paddingTop: '144px',
          paddingBottom: '80px',
          background: 'linear-gradient(135deg, #01142D 0%, #0A1F3D 60%, #1F65AB 100%)',
        }}
      >
        <div
          className="absolute right-0 bottom-0 select-none pointer-events-none"
          style={{
            fontSize: '320px',
            fontFamily: 'Poppins, sans-serif',
            fontWeight: 700,
            color: 'rgba(0,167,255,0.05)',
            lineHeight: 1,
          }}
        >
          CT
        </div>
        <div className="container-custom relative z-10">
          <div className="flex items-center gap-2 mb-4">
            <Link
              href="/"
              style={{ fontFamily: 'Poppins, sans-serif', fontSize: '14px', color: 'rgba(255,255,255,0.5)' }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = '#ffffff'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.5)'; }}
            >
              Home
            </Link>
            <span style={{ color: 'rgba(255,255,255,0.3)' }}>/</span>
            <span style={{ fontFamily: 'Poppins, sans-serif', fontSize: '14px', color: '#00A7FF' }}>Contact Us</span>
          </div>
          <h1
            className="mb-4"
            style={{
              fontFamily: 'Poppins, sans-serif',
              fontSize: 'clamp(36px, 5vw, 64px)',
              fontWeight: 200,
              color: '#ffffff',
              lineHeight: 1.2,
            }}
          >
            Let’s Connect
          </h1>
          <div style={{ width: '60px', height: '3px', background: '#00A7FF', marginBottom: '24px' }}></div>
          <p
            className="max-w-2xl"
            style={{ fontFamily: 'Poppins, sans-serif', fontSize: '18px', color: 'rgba(255,255,255,0.75)', lineHeight: 1.7 }}
          >
            Ready to find the RIGHT IT talent for your organization? Our team of expert recruiters is standing by to help you build the technology team that drives your business forward.
          </p>
        </div>
      </section>

      {/* Contact Form & Info - Pinnacle1 style: white bg */}
      <section className="section-padding" style={{ background: '#ffffff' }}>
        <div className="container-custom">
          <div className="grid lg:grid-cols-3 gap-10">
            {/* Contact Info */}
            <div className="lg:col-span-1">
              <h2
                className="mb-2"
                style={{ fontFamily: 'Poppins, sans-serif', fontSize: '28px', fontWeight: 400, color: 'rgba(0,0,0,0.8)' }}
              >
                Get In Touch
              </h2>
              <div style={{ width: '40px', height: '3px', background: '#00A7FF', marginBottom: '24px' }}></div>
              <p
                className="mb-8"
                style={{ fontFamily: 'Poppins, sans-serif', fontSize: '15px', color: '#48494B', lineHeight: 1.7 }}
              >
                Whether you’re looking to hire top IT talent or explore our consulting services, our team is ready to help.
              </p>

              <div className="space-y-5 mb-8">
                {[
                  { icon: 'M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z', label: 'Phone', value: '+1 (214) 555-0100', href: 'tel:+12145550100' },
                  { icon: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z', label: 'Email', value: 'info@tranceformright.com', href: 'mailto:info@tranceformright.com' },
                  { icon: 'M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z M15 11a3 3 0 11-6 0 3 3 0 016 0z', label: 'Headquarters', value: '24076 Gumspring Kiln Ter, Sterling, VA 20166', href: '#' },
                  { icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z', label: 'Business Hours', value: 'Mon–Fri: 8:00 AM – 6:00 PM CST', href: '#' },
                ].map((item) => (
                  <div key={item.label} className="flex items-start gap-4">
                    <div
                      className="w-10 h-10 flex items-center justify-center flex-shrink-0"
                      style={{ background: '#00A7FF' }}
                    >
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d={item.icon} />
                      </svg>
                    </div>
                    <div>
                      <div
                        className="mb-0.5"
                        style={{ fontFamily: 'Montserrat, sans-serif', fontSize: '11px', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#888888' }}
                      >
                        {item.label}
                      </div>
                      <a
                        href={item.href}
                        style={{ fontFamily: 'Poppins, sans-serif', fontSize: '14px', color: '#48494B' }}
                        onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = '#00A7FF'; }}
                        onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = '#48494B'; }}
                      >
                        {item.value}
                      </a>
                    </div>
                  </div>
                ))}
              </div>

              {/* Quick Links card */}
              <div
                className="p-6"
                style={{ background: '#FAFAFA', border: '1px solid #e8e8e8', borderTop: '3px solid #00A7FF' }}
              >
                <h3
                  className="mb-4"
                  style={{ fontFamily: 'Poppins, sans-serif', fontSize: '18px', fontWeight: 400, color: 'rgba(0,0,0,0.8)' }}
                >
                  Quick Actions
                </h3>
                <div className="space-y-3">
                  {[
                    { label: 'View Our Services', href: '/services' },
                    { label: 'See Our Client Portfolio', href: '/clients' },
                    { label: 'Learn About Us', href: '/about' },
                  ].map((link, i) => (
                    <Link
                      key={link.href}
                      href={link.href}
                      className="flex items-center gap-3 transition-colors duration-200"
                      style={{ fontFamily: 'Poppins, sans-serif', fontSize: '14px', color: '#48494B' }}
                      onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = '#00A7FF'; }}
                      onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = '#48494B'; }}
                    >
                      <svg className="w-4 h-4" style={{ color: '#00A7FF' }} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                      </svg>
                      {link.label}
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div
                className="p-8 md:p-10"
                style={{ background: '#ffffff', border: '1px solid #e8e8e8', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', borderTop: '3px solid #00A7FF' }}
              >
                {submitted ? (
                  <div className="text-center py-12">
                    <div
                      className="w-20 h-20 flex items-center justify-center mx-auto mb-6"
                      style={{ background: '#00A7FF' }}
                    >
                      <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <h3
                      className="mb-3"
                      style={{ fontFamily: 'Poppins, sans-serif', fontSize: '28px', fontWeight: 400, color: 'rgba(0,0,0,0.8)' }}
                    >
                      Message Sent Successfully!
                    </h3>
                    <p
                      className="mb-6"
                      style={{ fontFamily: 'Poppins, sans-serif', fontSize: '16px', color: '#48494B', lineHeight: 1.7 }}
                    >
                      Thank you for reaching out to TranceformRIGHT. One of our expert consultants will contact you within 24 business hours.
                    </p>
                    <button
                      onClick={() => { setSubmitted(false); setForm(initialForm); }}
                      className="inline-flex items-center justify-center px-8 py-3.5 font-semibold transition-all duration-300 hover:opacity-90"
                      style={{ background: '#00A7FF', color: '#ffffff', fontFamily: 'Montserrat, sans-serif', fontSize: '14px' }}
                    >
                      Send Another Message
                    </button>
                  </div>
                ) : (
                  <>
                    <h2
                      className="mb-2"
                      style={{ fontFamily: 'Poppins, sans-serif', fontSize: '28px', fontWeight: 400, color: 'rgba(0,0,0,0.8)' }}
                    >
                      Send Us a Message
                    </h2>
                    <div style={{ width: '40px', height: '3px', background: '#00A7FF', marginBottom: '24px' }}></div>

                    {/* Inquiry Type Toggle */}
                    <div className="flex gap-0 mb-8" style={{ border: '1px solid #e8e8e8' }}>
                      {[
                        { value: 'client', label: "I'm Hiring" },
                        { value: 'candidate', label: "I'm a Candidate" },
                        { value: 'general', label: 'General Inquiry' },
                      ].map((type) => (
                        <button
                          key={type.value}
                          type="button"
                          onClick={() => setForm((prev) => ({ ...prev, inquiryType: type.value }))}
                          className="flex-1 py-3 text-sm font-semibold transition-all duration-200"
                          style={{
                            fontFamily: 'Montserrat, sans-serif',
                            fontSize: '13px',
                            fontWeight: form.inquiryType === type.value ? 600 : 400,
                            color: form.inquiryType === type.value ? '#ffffff' : '#48494B',
                            background: form.inquiryType === type.value ? '#00A7FF' : '#ffffff',
                          }}
                        >
                          {type.label}
                        </button>
                      ))}
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-5">
                      <div className="grid md:grid-cols-2 gap-5">
                        <div>
                          <label
                            className="block mb-1.5"
                            style={{ fontFamily: 'Poppins, sans-serif', fontSize: '13px', fontWeight: 500, color: 'rgba(0,0,0,0.7)' }}
                          >
                            First Name *
                          </label>
                          <input
                            type="text"
                            name="firstName"
                            value={form.firstName}
                            onChange={handleChange}
                            required
                            placeholder="John"
                            className="input-field"
                          />
                        </div>
                        <div>
                          <label
                            className="block mb-1.5"
                            style={{ fontFamily: 'Poppins, sans-serif', fontSize: '13px', fontWeight: 500, color: 'rgba(0,0,0,0.7)' }}
                          >
                            Last Name *
                          </label>
                          <input
                            type="text"
                            name="lastName"
                            value={form.lastName}
                            onChange={handleChange}
                            required
                            placeholder="Smith"
                            className="input-field"
                          />
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-5">
                        <div>
                          <label
                            className="block mb-1.5"
                            style={{ fontFamily: 'Poppins, sans-serif', fontSize: '13px', fontWeight: 500, color: 'rgba(0,0,0,0.7)' }}
                          >
                            Email Address *
                          </label>
                          <input
                            type="email"
                            name="email"
                            value={form.email}
                            onChange={handleChange}
                            required
                            placeholder="john@company.com"
                            className="input-field"
                          />
                        </div>
                        <div>
                          <label
                            className="block mb-1.5"
                            style={{ fontFamily: 'Poppins, sans-serif', fontSize: '13px', fontWeight: 500, color: 'rgba(0,0,0,0.7)' }}
                          >
                            Phone Number
                          </label>
                          <input
                            type="tel"
                            name="phone"
                            value={form.phone}
                            onChange={handleChange}
                            placeholder="+1 (214) 555-0100"
                            className="input-field"
                          />
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-5">
                        <div>
                          <label
                            className="block mb-1.5"
                            style={{ fontFamily: 'Poppins, sans-serif', fontSize: '13px', fontWeight: 500, color: 'rgba(0,0,0,0.7)' }}
                          >
                            Company Name
                          </label>
                          <input
                            type="text"
                            name="company"
                            value={form.company}
                            onChange={handleChange}
                            placeholder="Acme Corporation"
                            className="input-field"
                          />
                        </div>
                        <div>
                          <label
                            className="block mb-1.5"
                            style={{ fontFamily: 'Poppins, sans-serif', fontSize: '13px', fontWeight: 500, color: 'rgba(0,0,0,0.7)' }}
                          >
                            Job Title
                          </label>
                          <input
                            type="text"
                            name="jobTitle"
                            value={form.jobTitle}
                            onChange={handleChange}
                            placeholder="VP of Technology"
                            className="input-field"
                          />
                        </div>
                      </div>

                      <div>
                        <label
                          className="block mb-1.5"
                          style={{ fontFamily: 'Poppins, sans-serif', fontSize: '13px', fontWeight: 500, color: 'rgba(0,0,0,0.7)' }}
                        >
                          Service of Interest
                        </label>
                        <div className="relative">
                          <select
                            name="service"
                            value={form.service}
                            onChange={handleChange}
                            className="input-field appearance-none pr-10"
                          >
                            <option value="">Select a service...</option>
                            {services.map((s) => (
                              <option key={s} value={s}>{s}</option>
                            ))}
                          </select>
                          <svg
                            className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none"
                            style={{ color: '#888888' }}
                            fill="none"
                            stroke="currentColor"
                            strokeWidth={2}
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </div>

                      <div>
                        <label
                          className="block mb-1.5"
                          style={{ fontFamily: 'Poppins, sans-serif', fontSize: '13px', fontWeight: 500, color: 'rgba(0,0,0,0.7)' }}
                        >
                          Message *
                        </label>
                        <textarea
                          name="message"
                          value={form.message}
                          onChange={handleChange}
                          required
                          rows={5}
                          placeholder="Tell us about your talent needs, project timeline, and any specific requirements..."
                          className="input-field resize-none"
                        />
                      </div>

                      <button
                        type="submit"
                        disabled={loading}
                        className="w-full py-4 font-semibold transition-all duration-300 hover:opacity-90 disabled:opacity-60"
                        style={{ background: '#00A7FF', color: '#ffffff', fontFamily: 'Montserrat, sans-serif', fontSize: '15px' }}
                      >
                        {loading ? 'Sending...' : 'Send Message'}
                      </button>
                    </form>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Office Locations - Pinnacle1 style: light bg */}
      <section className="section-padding" style={{ background: '#FAFAFA' }}>
        <div className="container-custom">
          <div className="text-center mb-12">
            <h6
              className="mb-3"
              style={{ fontFamily: 'Poppins, sans-serif', fontSize: '14px', fontWeight: 600, color: '#1F65AB', letterSpacing: '0.1em', textTransform: 'uppercase' }}
            >
              Our Offices
            </h6>
            <h2
              className="mb-4"
              style={{ fontFamily: 'Poppins, sans-serif', fontSize: '40px', fontWeight: 100, color: 'rgba(0,0,0,0.6)' }}
            >
              Office Locations
            </h2>
            <div style={{ width: '48px', height: '3px', background: '#00A7FF', margin: '0 auto' }}></div>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {offices.map((office, i) => (
              <div
                key={office.city}
                className="p-8 transition-all duration-300"
                style={{
                  background: '#ffffff',
                  border: '1px solid #e8e8e8',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                  borderTop: `3px solid ${i % 2 === 0 ? '#00A7FF' : '#01142D'}`,
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.boxShadow = '0 8px 32px rgba(0,145,221,0.15)';
                  (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.boxShadow = '0 2px 12px rgba(0,0,0,0.06)';
                  (e.currentTarget as HTMLElement).style.transform = 'translateY(0)';
                }}
              >
                {office.isHQ && (
                  <span
                    className="inline-block mb-3 px-3 py-1 text-xs font-semibold"
                    style={{ background: '#00A7FF', color: '#ffffff', fontFamily: 'Montserrat, sans-serif' }}
                  >
                    Headquarters
                  </span>
                )}
                <h3
                  className="mb-4"
                  style={{ fontFamily: 'Poppins, sans-serif', fontSize: '22px', fontWeight: 400, color: 'rgba(0,0,0,0.8)' }}
                >
                  {office.city}
                </h3>
                <div className="space-y-3">
                  <p style={{ fontFamily: 'Poppins, sans-serif', fontSize: '14px', color: '#48494B', lineHeight: 1.6 }}>
                    {office.address}<br />{office.cityState}
                  </p>
                  <a
                    href={`tel:${office.phone.replace(/[^+0-9]/g, '')}`}
                    style={{ fontFamily: 'Poppins, sans-serif', fontSize: '14px', color: '#00A7FF', display: 'block' }}
                  >
                    {office.phone}
                  </a>
                  <a
                    href={`mailto:${office.email}`}
                    style={{ fontFamily: 'Poppins, sans-serif', fontSize: '14px', color: '#00A7FF', display: 'block' }}
                  >
                    {office.email}
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section - Pinnacle1 style */}
      <section className="section-padding" style={{ background: '#ffffff' }}>
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h6
                className="mb-3"
                style={{ fontFamily: 'Poppins, sans-serif', fontSize: '14px', fontWeight: 600, color: '#1F65AB', letterSpacing: '0.1em', textTransform: 'uppercase' }}
              >
                Common Questions
              </h6>
              <h2
                className="mb-4"
                style={{ fontFamily: 'Poppins, sans-serif', fontSize: '40px', fontWeight: 100, color: 'rgba(0,0,0,0.6)' }}
              >
                Frequently Asked Questions
              </h2>
              <div style={{ width: '48px', height: '3px', background: '#00A7FF', margin: '0 auto' }}></div>
            </div>
            <div className="space-y-3">
              {faqs.map((faq, i) => (
                <div
                  key={faq.q}
                  style={{
                    background: '#ffffff',
                    border: '1px solid #e8e8e8',
                    borderLeft: openFaq === i ? '3px solid #00A7FF' : '3px solid transparent',
                    transition: 'all 0.2s ease',
                  }}
                >
                  <button
                    className="w-full flex items-center justify-between p-6 text-left"
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  >
                    <span
                      style={{ fontFamily: 'Poppins, sans-serif', fontSize: '16px', fontWeight: 400, color: 'rgba(0,0,0,0.8)', lineHeight: 1.4 }}
                    >
                      {faq.q}
                    </span>
                    <svg
                      className="w-5 h-5 flex-shrink-0 ml-4 transition-transform duration-200"
                      style={{ color: '#00A7FF', transform: openFaq === i ? 'rotate(180deg)' : 'none' }}
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {openFaq === i && (
                    <div
                      className="px-6 pb-6"
                      style={{ fontFamily: 'Poppins, sans-serif', fontSize: '15px', color: '#48494B', lineHeight: 1.7 }}
                    >
                      {faq.a}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
